import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { ctaRadius } from '@/lib/brandGradient'
import { cn } from '@/lib/cn'
import { glassSurfaceSoft } from '@/lib/glassSurface'
import { highlightMatch } from '@/lib/highlightMatch'
import commentsIcon from '@/assets/Group 8862.svg'
import eyeOutlineIcon from '@/assets/mdi_eye-outline.svg'
import trendingFlameIcon from '@/assets/streamline_trending-content-remix.svg'
import { SiteClockIcon } from '@/components/icons/SiteClockIcon'
import { ShareModal } from '@/components/share/ShareModal'
import { ShareForwardIcon } from '@/components/share/shareIcons'
import { formatVoteCount, VotePill } from '@/components/vote/VotePill'
import type { ShareSubject } from '@/lib/share'
import type { IdeaStatus, ScoreBreakdown } from '@/services/ideasService'

/** Design spec for explore / home idea cards. */
export const IDEA_CARD_WIDTH_PX = 400

/** `stretch` fills grid cells (explore / dashboard); `fixed` caps width (home). */
export type IdeaCardWidthMode = 'fixed' | 'stretch'
export const IDEA_CARD_HEIGHT_PX = 216

/** Renders text with a hard line-cap (1 or 2 lines) and an instant hover
 *  popover that ONLY appears when the rendered text is actually overflowing —
 *  i.e. it was truncated. Detects overflow by comparing scrollHeight against
 *  the clamped clientHeight, recomputed on resize via ResizeObserver and
 *  whenever the source string changes. */
function TruncatingText({
  text,
  lines,
  className,
  fullText,
  children,
}: {
  text: string
  lines: 1 | 2
  className: string
  /** Used in the popover. Defaults to `text`; pass when `children` is highlight-wrapped. */
  fullText?: string
  /** Optional rendered content (e.g. highlighted match). Falls back to `text`. */
  children?: ReactNode
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [overflowing, setOverflowing] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      if (lines === 1) {
        setOverflowing(el.scrollWidth > el.clientWidth + 1)
      } else {
        setOverflowing(el.scrollHeight > el.clientHeight + 1)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, lines])

  return (
    <span className="group/trunc relative block min-w-0">
      <span
        ref={ref}
        className={cn(
          'block min-w-0',
          lines === 1
            ? 'truncate'
            : 'line-clamp-2 [overflow-wrap:anywhere] break-words',
          className,
        )}
      >
        {children ?? text}
      </span>
      {overflowing ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute top-full left-0 z-30 mt-1 hidden max-w-[min(320px,calc(100vw-2rem))] min-w-[200px] rounded-md bg-[#0d0d0d] px-3 py-2 text-[13px] leading-relaxed font-normal [overflow-wrap:anywhere] whitespace-pre-wrap text-white opacity-0 shadow-xl ring-1 ring-white/10 transition-opacity duration-75 group-focus-within/trunc:block group-focus-within/trunc:opacity-100 group-hover/trunc:block group-hover/trunc:opacity-100"
        >
          {fullText ?? text}
        </span>
      ) : null}
    </span>
  )
}

/** Master industries are stored as compound names ("Technology/Software",
 *  "Retail/E-Commerce"). On idea cards we show only the leading segment so
 *  the pill stays compact. The full value is preserved for filter callbacks
 *  and aria-label so behavior is unchanged. */
const shortIndustry = (name: string | null | undefined) =>
  (name ?? '').split('/')[0]?.trim() || name || ''

function ideaScoreLabel(score: number): string {
  if (score >= 80) return 'Strong'
  if (score >= 50) return 'Moderate'
  return 'Weak'
}

function formatAuthorShort(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return fullName
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase()
  return `${parts[0]} ${lastInitial}.`
}

export type IdeaCardData = {
  id: string
  category: string
  score: number
  title: string
  description: string
  author: string
  /** Public profile slug when the list/detail API includes it. */
  authorHandle?: string | null
  /** Net score: upvotes - downvotes. Can be negative. */
  votes: number
  /** Per-viewer state — server-supplied when the BE attaches it to card payloads. */
  hasUpvoted?: boolean
  hasDownvoted?: boolean
  timeLeft: string
  /** When true, time-left label uses a red tone (e.g. ending soon) */
  submittedAt?: string | null
  timeUrgent?: boolean
  /** Lifecycle state — when set, a status pill is rendered next to the category. */
  status?: IdeaStatus
  /** Owner cards: current visibility. Drives the Public/Private toggle. */
  isPrivate?: boolean
  /**
   * When `status` is `live`, set if the review window has opened (validators active).
   * If present → green **Live** pill; if absent → yellow **In review** pill.
   */
  reviewOpenedAt?: string | null
  /** Server-supplied breakdown for the score popover. Null until the
   *  report cron has run for this idea. */
  breakdown?: ScoreBreakdown | null
  /**
   * Automated status badge, computed server-side by badge.service.js. The
   * backend sends the single highest-priority badge (hot / trending / closing
   * soon / needs validators / expert verified), or null when none applies.
   * Rendered as a pill in the card header — see `badgePresentation`.
   */
  badge?: { key: string; label: string } | null
  /** Shown in the comments pill when provided. */
  commentCount?: number
  /** Shown with the eye icon when provided (e.g. 4500 → 4.5K). */
  viewCount?: number
  industryTrendingCount?: number
}

const STATUS_LABEL: Record<IdeaStatus, string> = {
  live: 'Live',
  processed: 'Report ready',
  archived: 'Archived',
}

/** Pill accent icons — inline next to status label (h-3 to match 11px text). */
const StatusLiveIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <circle cx="6" cy="6" r="2.25" fill="currentColor" opacity={0.95} />
    <circle
      cx="6"
      cy="6"
      r="4.5"
      stroke="currentColor"
      strokeWidth={1}
      opacity={0.45}
    />
    <circle
      cx="6"
      cy="6"
      r="5.25"
      stroke="currentColor"
      strokeWidth={0.75}
      opacity={0.22}
    />
  </svg>
)

const StatusClockIcon = ({ className }: { className?: string }) => (
  <SiteClockIcon className={cn('h-3 w-3 shrink-0', className)} />
)

const StatusReportIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M3 2.25h4.5L9 3.75v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
    <path
      d="M7.25 2.25v1.5H9"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m4.25 7.35 1.15 1.15 2.35-2.35"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const StatusArchivedIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M2.5 4.5h7v1.75a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V4.5Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
    <path
      d="M4 2.75h4v1.75H4V2.75Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
    <path
      d="M4 8v1.25a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V8"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
    />
  </svg>
)

/** Theme-aware copy on glass cards (white in light mode, #141414 in dark). */
const cardTitleClass = 'text-[var(--foreground)] dark:text-white'
const cardMetaClass = 'text-[var(--foreground-muted)] dark:text-white/50'
const cardMetaDotClass = 'text-[var(--foreground-subtle)] dark:text-white/25'
const cardDescClass = 'text-[var(--foreground-muted)] dark:text-white/55'
const cardAuthorClass = 'text-[var(--foreground-subtle)] dark:text-white/45'
const cardViewsClass = 'text-[var(--foreground-muted)] dark:text-[#ffffffa6]'
const cardFooterDivider = 'border-black/[0.08] dark:border-white/[0.06]'
// `basis-[110px]` keeps the comfortable width when the row has room (home /
// explore), while `min-w-0` lets the pill shrink and truncate its label on
// narrow cards (profile grid) so the Validate CTA never wraps to a new row.
const cardCommentPill =
  'inline-flex h-[30px] min-w-0 basis-[132px] items-center justify-center gap-1.5 rounded-full border border-black/[0.1] bg-black/[0.03] px-2.5 text-[12px] font-medium text-[var(--foreground-muted)] dark:border-white/[0.12] dark:bg-white/[0.04] dark:text-[#ffffffb3]'
const cardOutlineBtn = `inline-flex h-[34px] min-w-[84px] shrink-0 items-center justify-center ${ctaRadius} border border-black/[0.14] bg-black/[0.03] px-4 text-[13px] font-semibold text-[var(--foreground)] transition hover:border-black/[0.22] hover:bg-black/[0.06] dark:border-white/20 dark:bg-white/[0.04] dark:text-[#ffffffeb] dark:hover:border-white/30 dark:hover:bg-white/[0.08]`
const cardShareBtn =
  'absolute right-3 z-20 grid h-7 w-7 place-items-center rounded-full border border-black/[0.1] bg-black/[0.04] text-[var(--foreground-muted)] transition hover:bg-black/[0.07] hover:text-[var(--foreground)] dark:border-white/12 dark:bg-white/[0.06] dark:text-white/75 dark:hover:bg-white/[0.12] dark:hover:text-white'

/** Top-right corner tabs — light tints on white cards; dark tints in .dark. */
const cornerPill = {
  emerald:
    'bg-emerald-500/12 text-emerald-700 ring-1 ring-inset ring-emerald-500/25 dark:bg-[#1a3328] dark:text-emerald-400 dark:ring-0',
  amber:
    'bg-amber-500/12 text-amber-800 ring-1 ring-inset ring-amber-500/25 dark:bg-[#2e2418] dark:text-amber-300 dark:ring-0',
  gold: 'bg-amber-500/12 text-amber-800 ring-1 ring-inset ring-amber-500/25 dark:bg-[#2e2418] dark:text-[#E8B86D] dark:ring-0',
  red: 'bg-red-500/12 text-red-700 ring-1 ring-inset ring-red-500/25 dark:bg-[#2e1818] dark:text-red-400 dark:ring-0',
  violet:
    'bg-violet-500/12 text-violet-700 ring-1 ring-inset ring-violet-500/25 dark:bg-[#2a2240] dark:text-violet-300 dark:ring-0',
  orange:
    'bg-orange-500/12 text-orange-800 ring-1 ring-inset ring-orange-500/25 dark:bg-[#2e2218] dark:text-orange-300 dark:ring-0',
  muted:
    'bg-zinc-500/10 text-zinc-600 ring-1 ring-inset ring-zinc-400/20 dark:bg-[#1f1f1f] dark:text-zinc-400 dark:ring-0',
} as const

/**
 * The lifecycle pill. `isOwnerCard` changes the wording rather than the state:
 * an owner looking at their own closed idea cares that the report is ready,
 * while a reader browsing the board just needs to know the window has shut.
 */
function statusPresentation(
  idea: IdeaCardData,
  isOwnerCard: boolean,
): {
  label: string
  pillClass: string
  Icon: typeof StatusLiveIcon
  tooltip: string
} | null {
  if (!idea.status) return null
  const opened =
    typeof idea.reviewOpenedAt === 'string' &&
    idea.reviewOpenedAt.trim().length > 0

  switch (idea.status) {
    case 'live':
      if (opened) {
        return {
          label: 'Live',
          pillClass: cornerPill.emerald,
          Icon: StatusLiveIcon,
          tooltip:
            'Your idea is live and available to other validators for review.',
        }
      }
      return {
        label: STATUS_LABEL.live,
        pillClass: cornerPill.amber,
        Icon: StatusClockIcon,
        tooltip:
          'Your idea is queued for review. It will open up to validators shortly.',
      }
    case 'processed':
      if (!isOwnerCard) {
        return {
          label: 'Closed',
          pillClass: cornerPill.orange,
          Icon: StatusReportIcon,
          tooltip:
            'The review window has closed. You can still read this idea and join the discussion.',
        }
      }
      return {
        label: STATUS_LABEL.processed,
        pillClass: cornerPill.orange,
        Icon: StatusReportIcon,
        tooltip:
          'The validation window has closed and a final report is ready for this idea.',
      }
    case 'archived':
      return {
        label: STATUS_LABEL.archived,
        pillClass: cornerPill.muted,
        Icon: StatusArchivedIcon,
        tooltip: 'This idea has been moved to the archive.',
      }
    default:
      return null
  }
}

/* ── Automated status badge ───────────────────────────────────────────────────
 * The backend (badge.service.js) attaches at most one badge per idea card,
 * already resolved by priority. This component just maps the badge `key` to a
 * label, colour and icon. Keep the keys in sync with badge.service.js.
 *
 *   expert_verified  — a paid expert validation report was delivered
 *   hot              — >=3 validations started in the last 24h
 *   trending         — weekly engagement in the platform's top 10%
 *   closing_soon     — validation window closes within 2 days
 *   needs_validators — fewer than 5 completed validations
 */

const BadgeExpertIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M6 1.25 9.75 2.6v3.05c0 2.4-1.6 4.2-3.75 5.1-2.15-.9-3.75-2.7-3.75-5.1V2.6L6 1.25Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
    <path
      d="m4.4 6 1.1 1.1L7.9 4.7"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const BadgeHotIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M6.5 1.25c.4 1.6-.6 2.4-1.3 3.1-.7.7-1.45 1.5-1.45 2.9A3.25 3.25 0 0 0 9 7.25c0-1.3-.6-2.2-1.2-3 .05.9-.35 1.45-.9 1.7.45-1.6-.1-3.4-.4-4.7Z"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinejoin="round"
    />
  </svg>
)

const BadgeTrendingIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M1.75 8.25 5 5l2 2 3.25-3.25"
      stroke="currentColor"
      strokeWidth={1.1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.75 3.75h2.5v2.5"
      stroke="currentColor"
      strokeWidth={1.1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const BadgeValidatorsIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={cn('h-3 w-3 shrink-0', className)}
    viewBox="0 0 12 12"
    fill="none"
  >
    <circle cx="4.5" cy="4" r="1.6" stroke="currentColor" strokeWidth={1} />
    <path
      d="M1.75 9.75c0-1.5 1.25-2.4 2.75-2.4s2.75.9 2.75 2.4"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
    />
    <path
      d="M8 3.1a1.5 1.5 0 0 1 0 2.9M8.75 7.5c1.05.25 1.75 1 1.75 2.25"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
    />
  </svg>
)

type BadgeUi = {
  label: string
  /** Short label for the top-right text pill (design). */
  displayLabel: string
  pillClass: string
  Icon: typeof BadgeExpertIcon
  tooltip: string
  /** Trending / hot — shown in industry hover pill instead of corner tab. */
  showOnIndustryHover?: boolean
}

/** Maps a server badge `key` to its rendered label, colour and icon. Returns
 *  null for an unknown key so a future backend badge can't crash old clients. */
function badgePresentation(
  badge: { key: string; label: string } | null | undefined,
): BadgeUi | null {
  if (!badge) return null
  // Tailwind only sees literal class strings, so each tone is spelled out in
  // full rather than built from `badge.key` — dynamic class names get purged.
  switch (badge.key) {
    case 'expert_verified':
      return {
        label: badge.label,
        displayLabel: badge.label,
        pillClass: cornerPill.violet,
        Icon: BadgeExpertIcon,
        tooltip:
          'A paid expert validation report has been completed for this idea.',
      }
    case 'hot':
      return {
        label: badge.label,
        displayLabel: 'Trending',
        pillClass: cornerPill.gold,
        Icon: BadgeHotIcon,
        tooltip: 'High validation activity in the last 24 hours.',
        showOnIndustryHover: true,
      }
    case 'trending':
      return {
        label: badge.label,
        displayLabel: 'Trending',
        pillClass: cornerPill.gold,
        Icon: BadgeTrendingIcon,
        tooltip: 'Among the top 10% of ideas by engagement this week.',
        showOnIndustryHover: true,
      }
    case 'closing_soon':
      return {
        label: badge.label,
        displayLabel: 'Closing Soon',
        pillClass: cornerPill.red,
        Icon: StatusClockIcon,
        tooltip: "The founder's validation window closes within 2 days.",
      }
    case 'needs_validators':
      return {
        label: badge.label,
        displayLabel: 'New',
        pillClass: cornerPill.emerald,
        Icon: BadgeValidatorsIcon,
        tooltip: 'Fewer than 5 validations so far. Be one of the first.',
      }
    default:
      return null
  }
}

/** Flush top-left corner tab — card TL 16px; badge bottom-right corner 5px. */
function CornerStatusBadge({
  label,
  pillClass,
  title,
}: {
  label: string
  pillClass: string
  title?: string
}) {
  return (
    <span
      title={title}
      data-idea-corner-pill
      className={cn(
        'absolute top-0 left-0 z-10 inline-flex items-center rounded-tl-[16px] rounded-br-[5px] px-3 py-1 text-[11px] font-medium tracking-wide',
        pillClass,
      )}
    >
      {label}
    </span>
  )
}

/** White-filled SVG assets — darken in light mode, native white in dark. */
const cardRasterIconClass =
  'shrink-0 object-contain opacity-70 brightness-0 dark:invert'

const CommentIcon = ({ className }: { className?: string }) => (
  <img
    src={commentsIcon}
    alt=""
    aria-hidden
    className={cn('h-3.5 w-3.5', cardRasterIconClass, className)}
    draggable={false}
  />
)

const EyeViewsIcon = ({ className }: { className?: string }) => (
  <img
    src={eyeOutlineIcon}
    alt=""
    aria-hidden
    className={cn('h-3 w-3', cardRasterIconClass, className)}
    draggable={false}
  />
)

/** Trending badge — shown above industry on hover (hot / trending ideas). */
function IndustryTrendingHoverPill({ label }: { label: string }) {
  return (
    <span className="inline-flex h-9 max-w-[200px] items-center gap-2 rounded-full border border-black/10 bg-white px-2.5 text-[12px] leading-none font-medium whitespace-nowrap text-[#111111] shadow-[0_8px_20px_-6px_rgba(0,0,0,0.18)] dark:border-white/25 dark:bg-[#141414] dark:text-[#e5e7eb] dark:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.85)]">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#D3191C]/15">
        <img
          src={trendingFlameIcon}
          alt=""
          aria-hidden
          className="h-3.5 w-3.5 object-contain"
          draggable={false}
        />
      </span>
      <span className="min-w-0 truncate">{label}</span>
    </span>
  )
}

function IndustryMeta({
  category,
  trendingCount,
  onIndustryClick,
}: {
  category: string
  trendingCount?: number
  onIndustryClick?: (industryName: string) => void
}) {
  const label = shortIndustry(category)
  if (!label) return null

  const industryClass = cn(
    'relative z-20 inline-block shrink-0 align-middle text-[14px] font-medium text-[var(--foreground-muted)] underline-offset-2 transition dark:text-white/55',
    onIndustryClick
      ? 'cursor-pointer hover:text-[var(--foreground)] hover:underline dark:hover:text-white/80'
      : 'hover:underline',
  )

  const trigger = onIndustryClick ? (
    <button
      type="button"
      data-industry-trigger
      className={industryClass}
      onClick={(e) => {
        e.stopPropagation()
        onIndustryClick(category)
      }}
      aria-label={`Filter by ${category}`}
    >
      {label}
    </button>
  ) : (
    <span data-industry-trigger className={industryClass}>
      {label}
    </span>
  )

  return (
    <span className="group/industry relative inline-flex max-w-full min-w-0 align-middle">
      {trendingCount != null && trendingCount > 0 ? (
        <span className="pointer-events-none absolute top-full left-14 z-50 mt-1.5 opacity-0 transition-opacity duration-150 group-focus-within/industry:opacity-100 group-hover/industry:opacity-100">
          <IndustryTrendingHoverPill label={`${trendingCount} trending`} />
        </span>
      ) : null}
      {trigger}
    </span>
  )
}

type IdeaCardProps = {
  idea: IdeaCardData
  /** Opens the idea's detail / conversation page. Whole card click target. */
  onValidate?: (id: string) => void
  /** Override the validate CTA label (e.g. "View" on already-validated ideas). */
  validateCtaLabel?: string
  /** Opens the idea validation report (dashboard "my ideas" cards). Takes
   *  precedence over `onValidate` when both are set. */
  onViewReport?: (id: string) => void
  /** Force the "by {author}" line on/off. Defaults to hidden when a report
   *  link is present (dashboard cards) and shown otherwise. */
  showAuthor?: boolean
  /** Stagger index for grid entrance animation (capped inside component). */
  staggerIndex?: number
  /** When set, the industry pill is clickable (e.g. open Validate Ideas filtered by this industry). */
  onIndustryClick?: (industryName: string) => void
  /** Active search query — when set, matched substrings in title/description
   *  are wrapped in a styled `<mark>`. */
  searchQuery?: string
  /** When set, the VotePill arrows become interactive. Each handler receives
   *  the idea id so the parent can manage optimistic state in one place. */
  onToggleUpvote?: (id: string) => void
  onToggleDownvote?: (id: string) => void
  /** Optional owner action (dashboard "My Idea"). */
  onWithdraw?: (id: string) => void
  withdrawBusy?: boolean
  /** Explore / dashboard grids: card fills the column. Home keeps `fixed`. */
  widthMode?: IdeaCardWidthMode
}

/** Dashboard / validate flows: full width in grid; `h-full` matches row height when siblings differ. */
const STAGGER_STEP_MS = 42
const STAGGER_CAP = 14

export const IdeaCard = ({
  idea,
  onValidate,
  validateCtaLabel,
  onViewReport,
  staggerIndex,
  showAuthor: showAuthorProp,
  onIndustryClick,
  searchQuery,
  onToggleUpvote,
  onToggleDownvote,
  onWithdraw,
  withdrawBusy = false,
  widthMode = 'fixed',
}: IdeaCardProps) => {
  const showAuthor = showAuthorProp ?? !onViewReport
  /**
   * Dashboard owner cards always show lifecycle status; explore and home show
   * the server badge instead. The one exception is a closed idea: it carries
   * its state wherever it appears, so a reader is never left guessing why the
   * validate button says View.
   */
  const isOwnerCard = Boolean(onViewReport || onWithdraw)
  const isClosed = idea.status === 'processed' || idea.status === 'archived'
  const statusUi =
    isOwnerCard || isClosed ? statusPresentation(idea, isOwnerCard) : null
  const badgeUi = badgePresentation(idea.badge)
  const commentCount = idea.commentCount ?? 0
  const commentLabel = `${commentCount} Comment${commentCount === 1 ? '' : 's'}`
  // Prefer the real distinct-viewer count when the endpoint supplies it
  // (including 0); only surfaces without a view_count fall back to votes.
  const viewsLabel = formatVoteCount(
    idea.viewCount != null ? idea.viewCount : Math.max(0, idea.votes),
  )
  const staggerMs =
    staggerIndex != null
      ? Math.min(staggerIndex, STAGGER_CAP) * STAGGER_STEP_MS
      : undefined
  const [shareOpen, setShareOpen] = useState(false)
  const shareSubject: ShareSubject = {
    kind: 'idea',
    ideaId: idea.id,
    title: idea.title,
    oneLinePitch: idea.description?.trim() || undefined,
    overallScore: idea.score > 0 ? idea.score : null,
    scoreLabel: ideaScoreLabel(idea.score),
    votes: idea.votes,
  }
  const cardClickable = Boolean(onViewReport || onValidate)
  const handleCardClick = () => {
    if (onViewReport) onViewReport(idea.id)
    else if (onValidate) onValidate(idea.id)
  }
  const handleCardClickEvent = (e: MouseEvent<HTMLElement>) => {
    // The share modal is portaled to <body>, but React replays its synthetic
    // clicks through the component tree back to this card. While it's open,
    // any click inside it (close, backdrop, buttons) must not open the idea.
    if (shareOpen) return
    if ((e.target as HTMLElement).closest('[data-industry-trigger]')) return
    if ((e.target as HTMLElement).closest('[data-idea-share]')) return
    // Don't navigate when the click is the user finishing a text selection
    // (highlighting card text shouldn't open the idea).
    if ((window.getSelection()?.toString().length ?? 0) > 0) return
    handleCardClick()
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <article
      role={cardClickable ? 'button' : undefined}
      tabIndex={cardClickable ? 0 : undefined}
      onClick={cardClickable ? handleCardClickEvent : undefined}
      onKeyDown={
        cardClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleCardClick()
              }
            }
          : undefined
      }
      className={cn(
        'motion-glass-card relative flex min-h-[216px] w-full min-w-0 flex-col overflow-visible rounded-[16px] pt-8 pr-4 pb-5 pl-5 text-left hover:border-black/[0.1] dark:hover:border-transparent',
        widthMode === 'stretch' && 'max-w-none',
        staggerIndex != null && 'motion-card-in',
        cardClickable && 'cursor-pointer',
        glassSurfaceSoft,
      )}
      style={
        {
          ...(widthMode === 'fixed'
            ? { maxWidth: IDEA_CARD_WIDTH_PX }
            : { maxWidth: 'none', width: '100%' }),
          ...(staggerMs != null
            ? { ['--motion-stagger' as string]: `${staggerMs}ms` }
            : {}),
        } as CSSProperties
      }
    >
      {statusUi ? (
        <CornerStatusBadge
          label={statusUi.label}
          pillClass={statusUi.pillClass}
          title={statusUi.tooltip}
        />
      ) : badgeUi && !badgeUi.showOnIndustryHover ? (
        <CornerStatusBadge
          label={badgeUi.displayLabel}
          pillClass={badgeUi.pillClass}
          title={badgeUi.tooltip}
        />
      ) : null}

      <button
        type="button"
        data-idea-share
        aria-label="Share idea"
        title="Share"
        onClick={(e) => {
          e.stopPropagation()
          setShareOpen(true)
        }}
        className={cn(cardShareBtn, 'top-2')}
      >
        <ShareForwardIcon className="h-3.5 w-3.5" />
      </button>

      <div className="mt-2 mb-3 flex cursor-default flex-col gap-1.5">
        <h3
          className={cn(
            'min-w-0 pr-10 text-[18px] leading-snug font-medium',
            cardTitleClass,
          )}
        >
          <TruncatingText
            text={idea.title}
            lines={1}
            className={cn(
              'text-[18px] leading-snug font-medium',
              cardTitleClass,
            )}
          >
            {highlightMatch(idea.title, searchQuery)}
          </TruncatingText>
        </h3>

        <div
          className={cn(
            'relative z-10 flex min-w-0 flex-wrap items-center gap-y-1 overflow-visible text-[14px] font-medium',
            cardMetaClass,
          )}
        >
          {idea.category ? (
            <>
              <IndustryMeta
                category={idea.category}
                trendingCount={idea.industryTrendingCount}
                onIndustryClick={onIndustryClick}
              />
              <span
                className={cn('mx-2 hidden sm:inline', cardMetaDotClass)}
                aria-hidden
              >
                ·
              </span>
              {/* Mobile only: push the timestamp + views onto their own line. */}
              <span aria-hidden className="basis-full sm:hidden" />
            </>
          ) : null}
          <span
            className={cn(
              'inline-flex items-center gap-1',
              idea.timeUrgent && 'text-red-600 dark:text-red-400/90',
            )}
          >
            <SiteClockIcon className="h-4 w-4 shrink-0 opacity-70" />
            {idea.timeLeft}
          </span>
          <span className={cn('mx-2', cardMetaDotClass)} aria-hidden>
            ·
          </span>
          <span
            className={cn('inline-flex items-center gap-1', cardViewsClass)}
          >
            <EyeViewsIcon />
            {viewsLabel}
          </span>
        </div>

        {idea.description ? (
          <div
            className={cn(
              'text-[14px] leading-relaxed font-medium',
              cardDescClass,
            )}
          >
            <TruncatingText
              text={idea.description}
              lines={1}
              className={cn(
                'text-[14px] leading-relaxed font-medium',
                cardDescClass,
              )}
            >
              {highlightMatch(idea.description, searchQuery)}
            </TruncatingText>
          </div>
        ) : null}
        {showAuthor ? (
          <p className={cn('text-[14px] font-medium', cardAuthorClass)}>
            by {formatAuthorShort(idea.author)}
          </p>
        ) : null}
      </div>

      <div className={cn('mt-auto border-t pt-3', cardFooterDivider)}>
        <div className="relative flex flex-nowrap items-center gap-2">
          <VotePill
            value={idea.votes}
            variant="card"
            compact={Math.abs(idea.votes) >= 1000}
            size="sm"
            interactive={
              (!idea.status || idea.status === 'live') &&
              Boolean(onToggleUpvote || onToggleDownvote)
            }
            upActive={idea.hasUpvoted ?? false}
            downActive={idea.hasDownvoted ?? false}
            onUp={
              (!idea.status || idea.status === 'live') && onToggleUpvote
                ? () => onToggleUpvote(idea.id)
                : undefined
            }
            onDown={
              (!idea.status || idea.status === 'live') && onToggleDownvote
                ? () => onToggleDownvote(idea.id)
                : undefined
            }
            ariaLabel="Vote"
          />
          <span className={cardCommentPill}>
            <CommentIcon />
            <span className="min-w-0 truncate">{commentLabel}</span>
          </span>

          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
            {onValidate && !onViewReport ? (
              <button
                type="button"
                className={cardOutlineBtn}
                onClick={(e) => {
                  e.stopPropagation()
                  onValidate(idea.id)
                }}
              >
                {validateCtaLabel ??
                  (idea.status && idea.status !== 'live' ? 'View' : 'Validate')}
              </button>
            ) : null}
            {onViewReport ? (
              <button
                type="button"
                className={cardOutlineBtn}
                onClick={(e) => {
                  e.stopPropagation()
                  onViewReport(idea.id)
                }}
              >
                View
              </button>
            ) : null}
            {onWithdraw ? (
              <button
                type="button"
                disabled={withdrawBusy}
                onClick={(e) => {
                  e.stopPropagation()
                  onWithdraw(idea.id)
                }}
                className={`inline-flex items-center border border-rose-400/35 bg-rose-500/10 px-3 py-1.5 text-sm font-semibold text-rose-700 transition hover:border-rose-500/45 hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:text-rose-200 dark:hover:border-rose-400/45 ${ctaRadius}`}
              >
                {withdrawBusy ? 'Withdrawing…' : 'Withdraw'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        subject={shareSubject}
      />
    </article>
  )
}
